import { useMemo } from 'react';
import useGraphqlQuery from '../../../hooks/useGraphqlQuery';
import { LINEAGE } from '../../../constants/queryKeys';
import { hashArray } from '../../../utils/hashing';
import { getColumnLineageQuery, getLineageAssetsQuery } from '../api';
import type {
  ApiLineage,
  ApiLineageNode,
  HopLimit,
  LineageQueryResponse,
} from '../types';
import { parseLineageResponse } from '../utils/lineageApiParsing';

export type { ApiLineage, ApiLineageNode };

type Props = {
  enabled: boolean;
  assetIds?: string[];
  columnIds?: string[];
  hopLimit?: HopLimit;
  perParentColumnsLimit?: number;
  selectionTooLarge: boolean;
};

const useLineageApi = ({
  enabled,
  assetIds,
  columnIds,
  hopLimit,
  perParentColumnsLimit,
  selectionTooLarge,
}: Props) => {
  const allIds = useMemo(
    () => [...(assetIds ?? []), ...(columnIds ?? [])],
    [assetIds, columnIds],
  );

  const selectedNodesHash = useMemo(
    () => (allIds.length ? hashArray(allIds) : ''),
    [allIds],
  );

  const hasColumnFilter = !!columnIds?.length;

  const {
    data: gqlLineageData,
    isFetching: isLineageFetching,
    isError: isLineageError,
  } = useGraphqlQuery<LineageQueryResponse>({
    queryKey: [
      LINEAGE,
      selectedNodesHash,
      hopLimit?.downstream ?? 1,
      hopLimit?.upstream ?? 1,
      perParentColumnsLimit,
      hasColumnFilter ? 'with-columns' : 'assets-only',
    ],
    query: hasColumnFilter ? getColumnLineageQuery : getLineageAssetsQuery,
    variables: {
      assetIds: assetIds ?? [],
      columnIds: columnIds?.length ? columnIds : null,
      downstreamHops: hopLimit?.downstream ?? 1,
      upstreamHops: hopLimit?.upstream ?? 1,
      perParentColumnsLimit: hasColumnFilter ? perParentColumnsLimit : null,
    },
    enabled: enabled && !!allIds.length && !selectionTooLarge,
  });

  const apiLineage: ApiLineage | null = useMemo(
    () => parseLineageResponse(gqlLineageData),
    [gqlLineageData],
  );

  const apiDagDepthBoundary = useMemo(
    () => ({
      incomingDepth: gqlLineageData?.lineage?.assets?.boundaries?.upstream || 0,
      outgoingDepth:
        gqlLineageData?.lineage?.assets?.boundaries?.downstream || 0,
    }),
    [gqlLineageData],
  );

  const exceededLimitParentAssets = useMemo(
    () => gqlLineageData?.lineage?.columns?.exceededLimitParentAssets || [],
    [gqlLineageData],
  );

  return {
    apiLineage,
    isLineageFetching,
    isLineageError,
    apiDagDepthBoundary,
    exceededLimitParentAssets,
    hasColumnFilter,
  };
};

export default useLineageApi;
